// frontend/src/hooks/useExternalPersonnel.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { externalPersonnelAPI } from '../services/api';
import toast from '../utils/toast';

// ============================================
// EXTERNAL / OUTSOURCED ASSET HOLDERS
// (e.g. JB office — not employees, no login)
// ============================================

export const useExternalPersonnel = (params = {}) => {
  return useQuery({
    queryKey: ['external-personnel', 'list', params],
    queryFn: async () => {
      const response = await externalPersonnelAPI.list(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
};

// Work sites for the office dropdown. Defaults to the JB office's project
// (P0000-018) but callers can override.
export const useExternalWorkSites = (projectCode = 'P0000-018') => {
  return useQuery({
    queryKey: ['external-personnel', 'work-sites', projectCode],
    queryFn: async () => {
      const response = await externalPersonnelAPI.getWorkSites(projectCode);
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes — work sites rarely change
  });
};

export const useRegisterExternalPersonnel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => externalPersonnelAPI.register(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['external-personnel'] });
      // The picker reads the staff list with include_external; refresh it too.
      queryClient.invalidateQueries({ queryKey: ['staff', 'list'] });
      const holder = response?.data;
      toast.success(
        holder?.staff_id
          ? `Registered ${holder.full_name || holder.staff_id}. Asset receipt links will be emailed to ${holder.work_email}.`
          : 'Holder registered.'
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to register holder.');
    },
  });
};

export const useOffboardExternalPersonnel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffId) => externalPersonnelAPI.offboard(staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-personnel'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      toast.success('Holder set to Inactive. They can no longer receive new assets.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to offboard holder.');
    },
  });
};

export const useResendReceiptLink = () => {
  return useMutation({
    mutationFn: (staffId) => externalPersonnelAPI.resendLink(staffId),
    onSuccess: (response) => {
      const n = response?.data?.pending_count;
      toast.success(
        n ? `Receipt link email queued for ${n} pending asset(s).` : 'Receipt link email queued.'
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'No assets awaiting acknowledgement.');
    },
  });
};
